import { WealthicaStorage } from '../../utils/storage';
import { logger } from '@/services/LoggingService';
import { NAVIGATION_STORAGE_KEYS, ReturnOptions, Wealthica2NavigationState } from './types';
import { clearAllNavigationData } from './safety';
import { toast } from 'sonner';

/**
 * Navigate back to a previously saved location, preserving optional params.
 */
export function returnToPreviousLocation(
  navigationState: Wealthica2NavigationState,
  options?: ReturnOptions
): void {
  const storedReturnUrl = WealthicaStorage.getItem(NAVIGATION_STORAGE_KEYS.RETURN_PATH);
  const returnUrl = storedReturnUrl || navigationState.returnUrl;

  logger.info('Wealthica2NavigationProvider', '[REDIRECT] Initiating return to previous location', {
    returnUrl,
    options,
    contextReturnSource: navigationState.returnSource,
    storageFlags: {
      state: WealthicaStorage.getItem(NAVIGATION_STORAGE_KEYS.STATE),
      provider: WealthicaStorage.getItem(NAVIGATION_STORAGE_KEYS.PROVIDER),
      returnUrl: WealthicaStorage.getItem(NAVIGATION_STORAGE_KEYS.RETURN_PATH),
      callbackProcessed: WealthicaStorage.getItem(NAVIGATION_STORAGE_KEYS.CALLBACK_PROCESSED),
      callbackTime: WealthicaStorage.getItem(NAVIGATION_STORAGE_KEYS.CALLBACK_TIME)
    },
    navigationState
  });

  if (returnUrl) {
    clearAllNavigationData();

    const returnLocation = new URL(returnUrl);
    if (options?.additionalParams) {
      Object.entries(options.additionalParams).forEach(([key, value]) => {
        returnLocation.searchParams.set(key, value);
      });
    }

    if (options?.showToast && options?.toastMessage) {
      toast.success(options.toastMessage);
    }

    const delay = options?.delay || 0;
    setTimeout(() => {
      logger.info('Wealthica2NavigationProvider', '[REDIRECT] Executing navigation to return URL', {
        returnUrl: returnLocation.toString(),
        replaceFlag: options?.replace
      });
      WealthicaStorage.removeItem(NAVIGATION_STORAGE_KEYS.RELOAD_COUNT);
      if (options?.replace) {
        window.location.replace(returnLocation.toString());
      } else {
        window.location.href = returnLocation.toString();
      }
    }, delay);
  } else {
    logger.warn('Wealthica2NavigationProvider', '[REDIRECT] No return URL found for navigation', {
      allStorageFlags: {
        state: WealthicaStorage.getItem(NAVIGATION_STORAGE_KEYS.STATE),
        provider: WealthicaStorage.getItem(NAVIGATION_STORAGE_KEYS.PROVIDER),
        returnUrl: WealthicaStorage.getItem(NAVIGATION_STORAGE_KEYS.RETURN_PATH),
      }
    });
    // Default to investments page if no return URL
    redirectToInvestments();
  }
}

/**
 * Redirect to the default investments page.
 */
export function redirectToInvestments(message?: string): void {
  const targetPath = '/investments';

  WealthicaStorage.removeItem(NAVIGATION_STORAGE_KEYS.STATE);
  WealthicaStorage.removeItem(NAVIGATION_STORAGE_KEYS.PROVIDER);
  WealthicaStorage.removeItem(NAVIGATION_STORAGE_KEYS.RETURN_PATH);
  // DO NOT clear CALLBACK_PROCESSED and CALLBACK_TIME - they prevent infinite loops

  logger.info('Wealthica2NavigationProvider', '[REDIRECT] Redirecting to investments', {
    destination: targetPath,
    method: 'window.location.replace',
    preservedCallbackFlags: {
      processed: WealthicaStorage.getItem(NAVIGATION_STORAGE_KEYS.CALLBACK_PROCESSED),
      time: WealthicaStorage.getItem(NAVIGATION_STORAGE_KEYS.CALLBACK_TIME)
    }
  });

  // Use window.location.replace to prevent history issues
  window.location.replace(targetPath);
}
